import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SpaceMonkeyLogo from '../../components/brand/SpaceMonkeyLogo';
import Logo from '../../components/brand/Logo';
import Icon from '../../components/brand/Icon';
import parkingApi from '../../services/parkingApi';
import './SpaceMonkeyValidatePage.css';

const LOT_SLUG = 'spacemonkey';

// Validation code format: SM-XXXXX (printed on the customer receipt).
const isValidFormat = (code) => /^SM-\d{5}$/.test(code.trim().toUpperCase());

const checkValidationCode = async (code, licensePlate) => {
  if (!isValidFormat(code)) {
    return { valid: false, reason: 'That code does not look right. Check the code on your receipt.' };
  }
  const res = await parkingApi.validateCode({
    slug: LOT_SLUG,
    code: code.trim().toUpperCase(),
    licensePlate: licensePlate.trim().toUpperCase(),
  });
  return res;
};

const SpaceMonkeyValidatePage = () => {
  const [plate, setPlate] = useState('');
  const [code, setCode] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [result, setResult] = useState(null);
  const [plateErr, setPlateErr] = useState('');
  const [codeErr, setCodeErr] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setPlateErr('');
    setCodeErr('');

    let valid = true;
    if (!plate.trim()) { setPlateErr('License plate is required'); valid = false; }
    if (!code.trim()) { setCodeErr('Validation code is required'); valid = false; }
    if (!valid) return;

    setStatus('loading');
    try {
      const res = await checkValidationCode(code, plate);
      setResult(res);
      setStatus(res.valid ? 'success' : 'error');
    } catch {
      setResult({ valid: false, reason: 'Something went wrong. Please try again.' });
      setStatus('error');
    }
  };

  const reset = () => {
    setStatus('idle');
    setResult(null);
    setPlate('');
    setCode('');
  };

  return (
    <div className="smv-page">
      <div className="smv-box">
        <div className="smv-brand">
          <Link to="/spacemonkey" className="smv-brand-logo">
            <SpaceMonkeyLogo size="sm" tagline={false} />
          </Link>
          <span className="smv-powered"><span>Powered by</span> <Logo size="sm" /></span>
        </div>

        <div className="smv-header">
          <h1>Customer Parking Validation</h1>
          <p>Enter your license plate and validation code from your receipt.</p>
        </div>

        {status === 'success' && (
          <div className="smv-success">
            <div className="smv-success-icon"><Icon name="check" size={38} /></div>
            <h2>Parking validated. No payment required.</h2>
            <div className="smv-validated-plate">{result.plate}</div>
            <p className="smv-validated-loc">Space Monkey Parking · Edinburg, TX · Valid today</p>
            <div className="smv-success-actions">
              <button className="smv-btn smv-btn-outline" onClick={reset}>Validate Another</button>
              <Link to="/spacemonkey" className="smv-btn smv-btn-ghost">Back</Link>
            </div>
          </div>
        )}

        {status !== 'success' && (
          <form onSubmit={handleSubmit} className="smv-form">
            <div className="smv-field">
              <label className="smv-label">License Plate *</label>
              <input
                className={`smv-input ${plateErr ? 'input-error' : ''}`}
                placeholder="e.g. ABC1234"
                value={plate}
                onChange={e => setPlate(e.target.value.toUpperCase())}
                maxLength={10}
                autoCapitalize="characters"
              />
              {plateErr && <div className="smv-field-error">{plateErr}</div>}
            </div>

            <div className="smv-field">
              <label className="smv-label">Validation Code *</label>
              <input
                className={`smv-input smv-code-input ${codeErr ? 'input-error' : ''}`}
                placeholder="SM-12345"
                value={code}
                onChange={e => setCode(e.target.value.toUpperCase().replace(/[^SM0-9-]/g, ''))}
                maxLength={8}
              />
              <div className="smv-code-hint">Find this code on your Space Monkey receipt.</div>
              {codeErr && <div className="smv-field-error">{codeErr}</div>}
            </div>

            {status === 'error' && (
              <div className="smv-error-box">
                <Icon name="alert" size={16} /> {result?.reason}
              </div>
            )}

            <button
              type="submit"
              className="smv-btn smv-btn-primary"
              disabled={status === 'loading'}
            >
              {status === 'loading' ? (
                <><span className="smv-spinner"></span> Checking...</>
              ) : 'Validate My Parking'}
            </button>

            <div className="smv-note">
              Validation is for Space Monkey customers only.
            </div>
          </form>
        )}

        <div className="smv-divider"></div>
        <div className="smv-not-customer">
          Not a Space Monkey customer?{' '}
          <Link to="/spacemonkey/pay">Buy a parking permit ($5 + $1 convenience fee)</Link>
        </div>
      </div>
    </div>
  );
};

export default SpaceMonkeyValidatePage;
